import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../../components/shared/ScrollBasic';
import { ImageView } from '@play-co/timestep-core/ui';
import TabbedView from '../../components/shared/tabs/TabbedView';
import { PetSpecies } from 'src/replicant/buruburu/pet';
import { LangBitmapOpts } from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import { VisitFriendTile } from './VisitFriendTile';
import { Feature } from 'src/replicant/analytics';

type SocialFriend = {
  id: string;
  name?: string;
  photo?: string;
  species: PetSpecies;
  lastLogin: number;
};
type CloseHandler = (result?: string) => void;

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 1015,
    darkerBg: false,
    titleIcon: { image: 'assets/ui/buruburu/visitfriend_icon.png' },
  };

  export const banner: ViewOpts = {
    backgroundColor: '#FFF3D9',
    x: 7,
    height: 103,
    y: 90,
  };
  banner.width = popup.width - 2 * banner.x;

  export const bannerBorder: ViewOpts = {
    backgroundColor: '#fdc381',
    width: banner.width,
    height: 3,
  };

  export const bannerIcon: ImageViewOpts = {
    image: 'assets/ui/buruburu/visitfriend_icon_display_banner.png',
    y: 8,
    width: 152,
    height: 98,
  };

  export const message: LangBitmapOpts = {
    size: 20,
    width: 382,
    height: banner.height,
    x: 175,
    y: banner.y,
    zIndex: 1,
    align: 'left',
    verticalAlign: 'center',
  };

  export const tab: ViewOpts = {
    backgroundColor: '#fdc381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    x: popup.width / 2,
    y: popup.height - 25 - 100 / 2,
    width: 259,
    height: 100,
    centerOnOrigin: true,
  };
}

export default class PopupVisitFriend extends PopupBasic {
  private tabs: TabbedView;
  private friendsList: ScrollBasic<SocialFriend>;
  private visitorsList: ScrollBasic<SocialFriend>;
  private buttonCancel: ButtonScaleViewWithText;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: () => opts.close(),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    const banner = this.box.addSubview(new View(style.banner));
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: -style.bannerBorder.height,
      }),
    );
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: style.message.height,
      }),
    );
    banner.addSubview(new ImageView(style.bannerIcon));
    this.message.updateOpts(style.message);

    this.tabs = new TabbedView({
      tabs: [
        {
          label: () => i18n('friendsVisit.popup.tab-friends'),
          tracking: {
            feature: Feature.VISIT._,
            $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.TAB_FRIENDS,
          },
        },
        {
          label: () => i18n('friendsVisit.popup.tab-visitors'),
          tracking: {
            feature: Feature.VISIT._,
            $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.TAB_VISITORS,
          },
        },
      ],
      alignment: 'left',
      superview: this.box,
      rect: {
        x: 30,
        y: 270,
        width: this.box.style.width - 60,
        height: 600,
      },
    });

    const views = this.tabs.getViews();
    views.forEach((view) => view.updateOpts(style.tab));
    const [friendsTab, visitorsTab] = views;

    this.friendsList = new ScrollBasic<SocialFriend>({
      superview: friendsTab,
      createItem: this.createItem.bind(this),
    });

    this.visitorsList = new ScrollBasic<SocialFriend>({
      superview: visitorsTab,
      createItem: this.createItem.bind(this),
    });

    this.buttonCancel = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.close'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close();
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.CLOSE,
        },
      }),
    );
  }

  init(opts: {
    friends: SocialFriend[];
    visitors: SocialFriend[];
    selectedTab?: 'friends' | 'visitors';
  }) {
    super.init({
      title: i18n('friendsVisit.popup.title'),
      message: i18n('friendsVisit.popup.message'),
    });

    this.friendsList.setItems(opts.friends);
    this.visitorsList.setItems(opts.visitors);

    if (opts.selectedTab === 'visitors') {
      this.tabs.setProps({ selectedTabIndex: 1 });
      this.tabs.setProps({});
    }
  }

  private createItem(superview: View, _: number, friend: SocialFriend) {
    return superview.addSubview(
      new VisitFriendTile({
        friend,
        onClick: async () => this.close(friend.id),
      }).view,
    );
  }
}
